import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { activateCard } from 'actions/cardActions';
import { trackCustomEvent } from 'utilities/app-insights';

/**
 * Custom hook for handling card activation logic.
 *
 * Consolidates activation state management, activation handlers,
 * and auto-updates to selected card status after successful activation.
 *
 * @remarks
 * Extracted from CardsList component to reduce complexity.
 *
 * @param selectedCard - Currently selected card object
 * @param setSelectedCard - Function to update selected card state (unstable from parent)
 * @param activateCardLoading - Loading state for card activation
 * @param activateCardLoaded - Success state for card activation
 * @param canReadWrite - User permission for card modifications
 * @param loggedInUser - Current logged-in user object
 * @param assignedAccountNumber - User's assigned account number
 * @param assignedGrandfatherClientNumber - User's grandfather client number
 */
export const useCardActivation = ({
  selectedCard,
  setSelectedCard,
  activateCardLoading,
  activateCardLoaded,
  canReadWrite,
  loggedInUser,
  assignedAccountNumber,
  assignedGrandfatherClientNumber,
}: {
  selectedCard: any;
  setSelectedCard: (card: any) => void;
  activateCardLoading: boolean;
  activateCardLoaded: boolean;
  canReadWrite: boolean;
  loggedInUser: any;
  assignedAccountNumber: string;
  assignedGrandfatherClientNumber: string;
}) => {
  const dispatch = useDispatch();

  // PATTERN: useRef for unstable callback (prevents infinite loops)
  // Store setSelectedCard in ref to avoid recreation cycles
  const setSelectedCardRef = useRef(setSelectedCard);
  useEffect(() => {
    setSelectedCardRef.current = setSelectedCard;
  });

  // Auto-update selected card status after successful activation
  // Legitimate effect: Responding to external data change (activation success)
  useEffect(() => {
    if (selectedCard?.status === 'Inactive' && activateCardLoaded) {
      setSelectedCardRef.current({ ...selectedCard, status: 'Active' });
    }
  }, [activateCardLoaded, selectedCard]);
  // Note: Using ref for setSelectedCard to avoid dependency recreation

  const handleActivateCardClick = () => {
    dispatch(activateCard(selectedCard?.cardNumber, loggedInUser.userId, assignedGrandfatherClientNumber));
    trackCustomEvent(
      'Submit Activate Card',
      loggedInUser,
      assignedAccountNumber,
      'Clicked Activate in Card Detail Modal to submit Activate card. ',
      { selectedAccount: selectedCard?.parent, selectedCard: selectedCard?.cardNumber }
    );
  };

  // Calculate if activate button should be disabled
  const btnInActivate =
    activateCardLoading || (selectedCard?.status === 'Inactive' && activateCardLoaded) || !canReadWrite;

  return {
    handleActivateCardClick,
    btnInActivate,
  };
};
